import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, mergeMap, catchError } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import * as HelocActions from './heloc.actions';
import { HelocService } from '../../services/heloc.service';

@Injectable()
export class HelocEffects {
  private actions$ = inject(Actions);
  private helocService = inject(HelocService);

  private getErrorMessage(error: unknown): string {
    console.error('HELOC API Error:', error);
    if (error instanceof HttpErrorResponse) {
      console.error('Error details:', error.error);
      if (error.status === 0) {
        return 'Unable to connect to the server. Please check your internet connection and try again.';
      }
      if (error.status === 400) {
        // Try to get validation errors
        if (typeof error.error === 'string') {
          return error.error;
        }
        if (error.error?.message) {
          return error.error.message;
        }
        if (error.error?.errors) {
          const errors = Object.values(error.error.errors).flat().join(' ');
          return errors || 'Please check your information and try again.';
        }
        return 'Please check your information and try again.';
      }
      if (error.status === 500) {
        if (error.error?.message) {
          return `Server error: ${error.error.message}`;
        }
        return 'Server error. Please try again later.';
      }
      return error.error?.message || error.message || 'An error occurred. Please try again.';
    }
    return 'An unexpected error occurred. Please try again.';
  }

  submitHelocApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HelocActions.submitHelocApplication),
      mergeMap(({ data }) =>
        this.helocService.submitApplication(data).pipe(
          map(application => HelocActions.submitHelocApplicationSuccess({ application })),
          catchError(error =>
            of(HelocActions.submitHelocApplicationFailure({ error: this.getErrorMessage(error) }))
          )
        )
      )
    )
  );

  getHelocApplications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HelocActions.getHelocApplications),
      mergeMap(({ pageNumber = 1, pageSize = 10 }) =>
        this.helocService.getApplications(pageNumber, pageSize).pipe(
          map(page => HelocActions.getHelocApplicationsSuccess({ page })),
          catchError(error =>
            of(HelocActions.getHelocApplicationsFailure({ error: this.getErrorMessage(error) }))
          )
        )
      )
    )
  );

  getHelocApplicationById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HelocActions.getHelocApplicationById),
      mergeMap(({ id }) =>
        this.helocService.getApplicationById(id).pipe(
          map(application => HelocActions.getHelocApplicationByIdSuccess({ application })),
          catchError(error =>
            of(HelocActions.getHelocApplicationByIdFailure({ error: this.getErrorMessage(error) }))
          )
        )
      )
    )
  );
}
